import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, connectDesk } from '../api'
import { Softphone, type SoftphoneStatus } from '../softphone'
import {
  Badge,
  Empty,
  Notice,
  Panel,
  Table,
  fmtDateTime,
  fmtDuration,
  fmtPhone,
  localTime,
} from '../components/ui'
import type {
  AgentState,
  EndpointStatus,
  CampaignSummary,
  DeskEvent,
  Disposition,
  LeadDetail,
  LeadSearchResult,
  LiveCall,
  MonitorMode,
  Session,
} from '../types'

const STATE_LABEL: Record<AgentState, string> = {
  logged_out: 'Offline',
  ready: 'Ready',
  reserved: 'Dialing',
  ringing: 'Ringing',
  talking: 'On call',
  wrap: 'Wrap-up',
  paused: 'Paused',
}

const PAUSE_CODES = ['Break', 'Lunch', 'Training', 'Meeting', 'Technical', 'Other']

export default function Dialer({ session }: { session: Session }) {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [campaignId, setCampaignId] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [state, setState] = useState<AgentState>('logged_out')
  const [call, setCall] = useState<LiveCall | null>(null)
  const [dispositions, setDispositions] = useState<Disposition[]>([])
  const [linkUp, setLinkUp] = useState(false)
  // Set while a supervisor the agent can hear is attached.
  const [supervisor, setSupervisor] = useState<MonitorMode | null>(null)
  const [phoneStatus, setPhoneStatus] = useState<SoftphoneStatus>('idle')
  const [notice, setNotice] = useState<{ kind: 'error' | 'info'; text: string } | null>(null)
  const [pendingCall, setPendingCall] = useState<string | null>(null)
  const [leadDetail, setLeadDetail] = useState<LeadDetail | null>(null)
  const [note, setNote] = useState('')
  const [script, setScript] = useState<string | null>(null)
  const [endpoint, setEndpoint] = useState<EndpointStatus | null>(null)
  const [dialing, setDialing] = useState(false)

  const softphone = useRef<Softphone | null>(null)

  useEffect(() => {
    api
      .campaigns()
      .then((c) => {
        const active = c.filter((x) => x.active)
        setCampaigns(active)
        if (active.length === 1) setCampaignId(active[0].id)
      })
      .catch(() => setNotice({ kind: 'error', text: 'Could not load campaigns' }))
  }, [])

  useEffect(() => {
    if (!campaignId) return
    api.campaignDispositions(campaignId).then(setDispositions).catch(() => setDispositions([]))
    api.campaign(campaignId).then((c) => setScript(c.script)).catch(() => setScript(null))
  }, [campaignId])

  // Asterisk's view of this agent's endpoint. The browser believing it
  // registered is a claim; this is the fact, and the two are shown separately
  // so a mismatch is visible rather than papered over.
  useEffect(() => {
    const poll = () => api.endpointStatus().then(setEndpoint).catch(() => setEndpoint(null))
    poll()
    const t = setInterval(poll, 10000)
    return () => clearInterval(t)
  }, [])

  // ---- softphone -----------------------------------------------------------
  useEffect(() => {
    if (!session.sip) return
    const audio = document.getElementById('impact-remote-audio') as HTMLAudioElement
    const phone = new Softphone(audio, (status, detail) => {
      setPhoneStatus(status)
      if (status === 'audio_blocked') {
        setNotice({ kind: 'error', text: 'The browser blocked audio. Click anywhere to enable it.' })
      }
      if (status === 'disconnected' && detail) {
        setNotice({ kind: 'error', text: `Phone disconnected: ${detail}` })
      }
    })
    softphone.current = phone
    phone.connect(session.sip).catch((e: Error) =>
      setNotice({ kind: 'error', text: `Phone could not register: ${e.message}` }),
    )
    return () => {
      void phone.disconnect()
      softphone.current = null
    }
  }, [session.sip])

  // ---- desk socket ---------------------------------------------------------
  const handleEvent = useCallback((raw: unknown) => {
    const e = raw as DeskEvent
    switch (e.type) {
      case 'snapshot':
        setState(e.state)
        setCall(e.call)
        if (e.campaign_id) setCampaignId(e.campaign_id)
        break
      case 'state_changed':
        setState(e.state)
        if (e.state === 'ready') { setCall(null); setLeadDetail(null); setNote('') }
        break
      case 'call_offered':
        setCall(e.call)
        break
      case 'call_connected':
        setCall((c) => (c ? { ...c, answered: true } : c))
        break
      case 'call_ended':
        setNotice(e.cause ? { kind: 'info', text: `Call ended — ${e.cause}` } : null)
        if (e.needs_disposition) setPendingCall(e.call_id)
        break
      case 'call_blocked':
        setNotice({ kind: 'error', text: `${e.reason_code} — ${e.message}` })
        break
      case 'monitored':
        // The agent is told the moment a supervisor becomes audible, and told
        // again when they leave. A whisper the agent does not know about would
        // have them hearing a voice with no explanation; a barge the agent does
        // not know about would have them talking over their own manager.
        setSupervisor(e.active ? e.mode : null)
        break
      case 'error':
        setNotice({ kind: 'error', text: e.message })
        break
    }
  }, [])

  useEffect(() => {
    if (!sessionId) return
    return connectDesk(sessionId, handleEvent, setLinkUp)
  }, [sessionId, handleEvent])

  // Pull the full lead record when a call comes up, so the agent sees history
  // and previous dispositions rather than just a name.
  useEffect(() => {
    if (!call?.lead_id) { setLeadDetail(null); return }
    api.lead(call.lead_id).then(setLeadDetail).catch(() => setLeadDetail(null))
  }, [call?.lead_id])

  const goOnline = async () => {
    try {
      const { session_id } = await api.agentLogin(campaignId)
      setSessionId(session_id)
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof ApiError ? err.message : 'Could not go online' })
    }
  }

  const goOffline = async () => {
    if (!sessionId) return
    await api.agentLogout(sessionId).catch(() => undefined)
    setSessionId(null)
    setState('logged_out')
    setCall(null)
  }

  const dial = async (phone: string, leadId?: string) => {
    if (!sessionId) return
    setNotice(null)
    try {
      await api.dial(sessionId, phone, leadId)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'blocked') {
        setNotice({ kind: 'error', text: `${err.reasonCode} — ${err.message}` })
      } else {
        setNotice({ kind: 'error', text: err instanceof ApiError ? err.message : 'Could not dial' })
      }
    }
  }

  const disposeCall = async (dispositionId: string) => {
    const callId = pendingCall ?? call?.call_id
    if (!sessionId || !callId) return
    await api
      .dispose(sessionId, callId, dispositionId, note || undefined)
      .catch((e: ApiError) => setNotice({ kind: 'error', text: e.message }))
    setPendingCall(null)
    setCall(null)
    setLeadDetail(null)
    setNote('')
  }

  const dialNext = async () => {
    if (!sessionId) return
    setNotice(null)
    setDialing(true)
    try {
      const r = await api.dialNext(sessionId)
      if (!r.dialed) {
        setNotice({ kind: 'info', text: r.reason ?? 'No eligible leads right now.' })
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'blocked') {
        setNotice({ kind: 'error', text: `${err.reasonCode} — ${err.message}` })
      } else {
        setNotice({ kind: 'error', text: err instanceof ApiError ? err.message : 'Could not dial' })
      }
    } finally {
      setDialing(false)
    }
  }

  const online = sessionId !== null
  const needsDisposition = state === 'wrap' || pendingCall !== null
  const campaign = campaigns.find((c) => c.id === campaignId) ?? null

  if (!online) {
    return (
      <GoOnline
        campaigns={campaigns}
        campaignId={campaignId}
        onSelect={setCampaignId}
        onGoOnline={goOnline}
        phoneStatus={phoneStatus}
        endpoint={endpoint}
        role={session.role}
        notice={notice}
        onDismiss={() => setNotice(null)}
      />
    )
  }

  return (
    <div className="page dialer">
      <header className="page-head">
        <div className="dialer-status">
          <div className={`state-pill state-${state}`}>
            <span className="state-dot" />
            {STATE_LABEL[state]}
          </div>
          <Indicator ok={phoneStatus === 'registered' || phoneStatus === 'on_leg'} label="Phone" />
          {/* Asterisk's answer, kept separate from the browser's. A green
              browser indicator next to a red endpoint one is the exact state
              that used to look healthy and could not take a call. */}
          <Indicator
            ok={endpoint?.registered === true}
            unknown={endpoint?.registered === null}
            label="Endpoint"
            title={endpoint?.reason ?? endpoint?.endpoint}
          />
          <Indicator ok={linkUp} label="Link" />
          {campaign && (
            <span className="dialer-campaign">
              {campaign.name}
              <span className="muted small"> · {campaign.dialable.toLocaleString()} dialable</span>
            </span>
          )}
        </div>
        <div className="page-actions">
          {state === 'paused' ? (
            <button className="primary" onClick={() => sessionId && api.setState(sessionId, 'ready')}>
              Resume
            </button>
          ) : (
            <PauseMenu onPause={(c) => sessionId && api.setState(sessionId, 'paused', c)} />
          )}
          <button className="ghost" onClick={goOffline}>Go offline</button>
        </div>
      </header>

      {/* Deliberately not dismissable. This is not a notification, it is the
          state of the call: a supervisor is audible right now, and it stays on
          screen for exactly as long as that is true. */}
      {supervisor && (
        <div className={`supervisor-banner ${supervisor}`}>
          {supervisor === 'whisper' ? (
            <>
              <strong>A supervisor is coaching you.</strong> They can hear the
              call and you can hear them. The customer cannot.
            </>
          ) : (
            <>
              <strong>A supervisor has joined the call.</strong> Everyone on the
              line can hear them, including the customer.
            </>
          )}
        </div>
      )}

      {notice && (
        <Notice kind={notice.kind === 'error' ? 'error' : 'info'} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <div className="dialer-grid">
        <Panel title="Call" className="dialer-call">
          <CallCard
            state={state}
            call={call}
            onHangup={() => sessionId && api.hangup(sessionId)}
            onMute={(m) => softphone.current?.setMuted(m)}
            onDtmf={(d) => softphone.current?.sendDtmf(d)}
          />
        </Panel>

        <Panel title={needsDisposition ? 'Disposition' : 'Dial'} className="dialer-work">
          {needsDisposition ? (
            <DispositionPad
              dispositions={dispositions}
              note={note}
              onNote={setNote}
              onSelect={disposeCall}
            />
          ) : (
            <DialPad
              campaignId={campaignId}
              disabled={state !== 'ready'}
              onDial={dial}
              onDialNext={dialNext}
              dialing={dialing}
            />
          )}
        </Panel>

        <Panel title="Lead" className="dialer-lead">
          <LeadWorkspace detail={leadDetail} call={call} script={script} />
        </Panel>
      </div>
    </div>
  )
}

// =============================================================== pieces ====

/**
 * A status light with three states, not two.
 *
 * `unknown` exists because "the media server is unreachable so we cannot tell"
 * is a different fact from "the phone is not registered", and showing the
 * former as the latter sends an agent to check a headset that was never the
 * problem.
 */
function Indicator({
  ok,
  unknown,
  label,
  title,
}: {
  ok: boolean
  unknown?: boolean
  label: string
  title?: string | null
}) {
  const cls = unknown ? 'unknown' : ok ? 'up' : 'down'
  const word = unknown ? 'unknown' : ok ? 'up' : 'down'
  return (
    <span className={`indicator ${cls}`} title={title || `${label}: ${word}`}>
      <span className="indicator-dot" />
      {label}
    </span>
  )
}

function PauseMenu({ onPause }: { onPause: (code: string) => void }) {
  return (
    <details className="pause-menu">
      <summary>Pause</summary>
      <ul>
        {PAUSE_CODES.map((c) => (
          <li key={c}>
            <button onClick={() => onPause(c)}>{c}</button>
          </li>
        ))}
      </ul>
    </details>
  )
}

function GoOnline({
  campaigns,
  campaignId,
  onSelect,
  onGoOnline,
  phoneStatus,
  endpoint,
  role,
  notice,
  onDismiss,
}: {
  endpoint: EndpointStatus | null
  role: Session['role']
  campaigns: CampaignSummary[]
  campaignId: string | null
  onSelect: (id: string) => void
  onGoOnline: () => void
  phoneStatus: SoftphoneStatus
  notice: { kind: 'error' | 'info'; text: string } | null
  onDismiss: () => void
}) {
  const phoneReady = phoneStatus === 'registered' || phoneStatus === 'on_leg'
  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Dialer</h1>
          <p className="muted">Agent station</p>
        </div>
        <Indicator ok={phoneReady} label="Phone" />
      </header>

      {notice && (
        <Notice kind={notice.kind === 'error' ? 'error' : 'info'} onDismiss={onDismiss}>
          {notice.text}
        </Notice>
      )}

      {/* An account with no endpoint is a dead end for an agent but a two-click
          fix for an admin, so admins get the route rather than the apology. */}
      {endpoint && !endpoint.provisioned && (
        <Notice kind="error">
          This account has no SIP endpoint, so it cannot take calls.{' '}
          {role === 'admin' ? (
            <button className="link" onClick={() => (location.hash = 'agents')}>
              Provision one under Agents → People →
            </button>
          ) : (
            <>An administrator needs to provision one under Agents → People.</>
          )}
        </Notice>
      )}

      {endpoint?.provisioned && endpoint.registered === false && (
        <Notice kind="warn">
          Endpoint <span className="mono">{endpoint.endpoint}</span> is not registered with the
          media server{endpoint.reason ? ` — ${endpoint.reason}` : ''}.
          {endpoint.known_to_asterisk === false && role === 'admin' && (
            <>
              {' '}
              The endpoint block is missing from <code>pjsip.conf</code>; re-create the agent under
              Agents → People to get the config to paste.
            </>
          )}
        </Notice>
      )}

      {endpoint?.registered === null && (
        <Notice kind="warn">
          The media server is unreachable, so phone registration cannot be confirmed. Check system
          health before going online.
        </Notice>
      )}

      <Panel title="Go online">
        {campaigns.length === 0 ? (
          <Empty
            message="No active campaigns."
            hint="A supervisor needs to create one and start it under Campaigns."
          />
        ) : (
          <>
            <ul className="campaign-list">
              {campaigns.map((c) => (
                <li key={c.id}>
                  <button
                    className={c.id === campaignId ? 'campaign selected' : 'campaign'}
                    onClick={() => onSelect(c.id)}
                  >
                    <span className="campaign-name">{c.name}</span>
                    <span className="muted small">{c.dialable.toLocaleString()} dialable</span>
                    <Badge tone="muted">{c.dial_mode}</Badge>
                  </button>
                </li>
              ))}
            </ul>
            <button className="primary" disabled={!campaignId || !phoneReady} onClick={onGoOnline}>
              Go online
            </button>
            <p className="muted small">
              {phoneReady
                ? 'Your phone will ring once and stay connected for the whole shift.'
                : 'Waiting for your phone to register…'}
            </p>
          </>
        )}
      </Panel>
    </div>
  )
}

function CallCard({
  state,
  call,
  onHangup,
  onMute,
  onDtmf,
}: {
  state: AgentState
  call: LiveCall | null
  onHangup: () => void
  onMute: (m: boolean) => void
  onDtmf: (d: string) => void
}) {
  const [muted, setMuted] = useState(false)
  const elapsed = useElapsed(call?.answered ? call.started_at : null)

  if (!call) {
    return (
      <div className="idle">
        <p className="muted">{state === 'paused' ? 'Paused' : 'Waiting for a call'}</p>
      </div>
    )
  }

  const name = call.lead
    ? [call.lead.first_name, call.lead.last_name].filter(Boolean).join(' ')
    : null

  return (
    <div className="live-call">
      <div className="call-head">
        <div>
          <div className="call-number">{fmtPhone(call.dialed_number)}</div>
          {name && <div className="call-name">{name}</div>}
        </div>
        <div className="call-timer">{call.answered ? elapsed : 'ringing'}</div>
      </div>

      {call.caller_id_used && (
        <p className="muted small">Presenting {fmtPhone(call.caller_id_used)}</p>
      )}

      <div className="call-actions">
        <button
          className={muted ? 'toggle on' : 'toggle'}
          onClick={() => { const n = !muted; setMuted(n); onMute(n) }}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
        <button className="danger" onClick={onHangup}>Hang up</button>
      </div>

      <div className="keypad">
        {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
          <button key={k} onClick={() => onDtmf(k)}>{k}</button>
        ))}
      </div>
    </div>
  )
}

function DialPad({
  campaignId,
  disabled,
  onDial,
  onDialNext,
  dialing,
}: {
  campaignId: string | null
  disabled: boolean
  onDial: (phone: string, leadId?: string) => void
  onDialNext?: () => void
  dialing?: boolean
}) {
  const [number, setNumber] = useState('')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LeadSearchResult[]>([])

  useEffect(() => {
    if (!campaignId || query.trim().length < 2) { setResults([]); return }
    const t = setTimeout(() => {
      api.searchLeads(campaignId, query.trim()).then(setResults).catch(() => setResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [campaignId, query])

  return (
    <div className="dial-panel">
      <form
        className="manual-dial"
        onSubmit={(e) => { e.preventDefault(); if (number.trim()) onDial(number.trim()) }}
      >
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="Number to dial"
          inputMode="tel"
          disabled={disabled}
        />
        <button className="primary" type="submit" disabled={disabled || !number.trim()}>
          Dial
        </button>
      </form>

      {onDialNext && (
        <button className="primary wide" onClick={onDialNext} disabled={disabled || dialing}>
          {dialing ? 'Dialling…' : 'Dial next lead'}
        </button>
      )}

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search leads by name or number"
        disabled={disabled}
      />

      <ul className="results">
        {results.map((r) => (
          <li key={r.id}>
            <button disabled={disabled} onClick={() => onDial(r.phone_e164, r.id)}>
              <span className="result-name">
                {[r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unnamed'}
              </span>
              <span className="result-meta">
                {fmtPhone(r.phone_e164)}
                {r.city ? ` · ${r.city}${r.state ? `, ${r.state}` : ''}` : ''}
                {r.attempt_count > 0 ? ` · ${r.attempt_count} attempts` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DispositionPad({
  dispositions,
  note,
  onNote,
  onSelect,
}: {
  dispositions: Disposition[]
  note: string
  onNote: (n: string) => void
  onSelect: (id: string) => void
}) {
  return (
    <div className="disposition-panel">
      <p className="muted small">The call is finished. Choose an outcome to take the next one.</p>
      <textarea
        rows={2}
        placeholder="Note (optional)"
        value={note}
        onChange={(e) => onNote(e.target.value)}
      />
      <div className="disposition-grid">
        {dispositions.map((d) => (
          <button
            key={d.id}
            className={`disposition${d.is_dnc ? ' dnc' : ''}${d.is_contact ? ' contact' : ''}`}
            onClick={() => onSelect(d.id)}
          >
            <span className="disposition-code">{d.code}</span>
            <span className="disposition-label">{d.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function LeadWorkspace({
  detail,
  call,
  script,
}: {
  detail: LeadDetail | null
  call: LiveCall | null
  script: string | null
}) {
  const lead = detail?.lead ?? null
  const snapshot = call?.lead ?? null

  if (!lead && !snapshot) {
    return (
      <>
        {script && (
          <div className="script">
            <h4>Script</h4>
            <p>{script}</p>
          </div>
        )}
        <Empty message="No lead attached." hint="Dial from the lead search to see their record here." />
      </>
    )
  }

  const first = lead?.first_name ?? snapshot?.first_name ?? ''
  const tz = localTime(lead?.timezone ?? snapshot?.timezone ?? null)

  return (
    <div className="lead-workspace">
      {detail && detail.dnc.length > 0 && (
        <Notice kind="error">Suppressed: {detail.dnc.join(', ')}</Notice>
      )}

      {script && (
        <div className="script">
          <h4>Script</h4>
          <p>{script.replace(/\{first_name\}/g, first || 'there')}</p>
        </div>
      )}

      <dl className="kv">
        <div><dt>Name</dt><dd>{[first, lead?.last_name ?? snapshot?.last_name].filter(Boolean).join(' ') || '—'}</dd></div>
        <div><dt>Location</dt><dd>{[lead?.city ?? snapshot?.city, lead?.state ?? snapshot?.state].filter(Boolean).join(', ') || '—'}</dd></div>
        {tz && <div><dt>Their time</dt><dd className="mono">{tz}</dd></div>}
        <div><dt>Email</dt><dd>{lead?.email ?? snapshot?.email ?? '—'}</dd></div>
        <div><dt>Attempts</dt><dd>{lead?.attempt_count ?? snapshot?.attempt_count ?? 0}</dd></div>
      </dl>

      {lead && Object.keys(lead.custom ?? {}).length > 0 && (
        <>
          <h4>Custom fields</h4>
          <dl className="kv">
            {Object.entries(lead.custom).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>
            ))}
          </dl>
        </>
      )}

      {detail && detail.history.length > 0 && (
        <>
          <h4>Previous attempts</h4>
          <Table head={['When', 'Agent', 'Outcome']}>
            {detail.history.slice(0, 8).map((h) => (
              <tr key={h.call_id}>
                <td className="muted">{fmtDateTime(h.at)}</td>
                <td>{h.agent ?? '—'}</td>
                <td>
                  {h.disposition_label ?? <span className="muted">no answer</span>}
                  {h.talk_sec ? <span className="muted small"> · {fmtDuration(h.talk_sec)}</span> : null}
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

function useElapsed(since: string | null): string {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!since) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [since])

  return useMemo(() => {
    if (!since) return '00:00'
    const secs = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000))
    return `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`
  }, [since, now])
}
